import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { API_URL } from "../redux/const";
import { encryptData, decryptData } from "../redux/_common/enode-decode";
import { getLoginObjDetails } from "../redux/login/login.selector";
import { updateUserAccessData } from "../redux/login/login.action";

/**
 * Keep a logged-in session's RBAC grants live.
 *
 * Grants (userAccessData) are baked in at login and persisted, so an admin
 * changing a role's rights wouldn't take effect until the user logged out and
 * back in. This hook re-fetches the current role's grants from the backend on
 * mount and whenever the tab regains focus, then patches loginData so the
 * sidebar, route guard and CRUD gating update in place — no re-login needed.
 *
 * Cheap: one small POST per focus (throttled). No-op when not logged in.
 */
const KEY = "RefreshUserAccessKey";
const MIN_INTERVAL_MS = 15000; // don't refetch more than once per 15s

const useLiveAccessRefresh = () => {
  const dispatch = useDispatch();
  const loginData: any = useSelector(getLoginObjDetails);
  const { UserTypeID, CompanyID, InstanceName } = loginData || {};
  const lastRun = useRef(0);

  useEffect(() => {
    if (!InstanceName || !UserTypeID) return; // not logged in / no role

    const refresh = async () => {
      const now = Date.now();
      if (now - lastRun.current < MIN_INTERVAL_MS) return;
      lastRun.current = now;
      try {
        const token = encryptData(
          { UserTypeID, CompanyID, InstanceName },
          KEY,
        );
        const res: any = await axios.post(`${API_URL}RefreshUserAccess`, {
          RefreshUserAccessToken: token,
        });
        const dec = res?.data?.response
          ? decryptData(res.data.response, KEY)
          : null;
        if (dec && Array.isArray(dec.userAccessData)) {
          dispatch(updateUserAccessData(dec.userAccessData));
        }
      } catch {
        /* silent — a failed refresh just leaves the last-known grants in place */
      }
    };

    refresh(); // on mount
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [UserTypeID, CompanyID, InstanceName, dispatch]);
};

export default useLiveAccessRefresh;
